import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pulse, Bell, MagnifyingGlass } from "@phosphor-icons/react";

const BARS = [42, 55, 38, 64, 72, 58, 80, 68, 90, 76, 95, 84];

const POOL = [
  { initials: "EM", name: "Eleanor M.", tags: ["APCM", "RPM"], tone: "#2E6FB0", status: "On track", dot: "#6FBE44" },
  { initials: "JR", name: "James R.", tags: ["CCM", "RPM"], tone: "#0E7C7C", status: "Review", dot: "#C77F2E" },
  { initials: "SA", name: "Sofia A.", tags: ["CoCM"], tone: "#16A394", status: "On track", dot: "#6FBE44" },
  { initials: "DP", name: "David P.", tags: ["AWV", "ACP"], tone: "#6FBE44", status: "Enrolled", dot: "#6FBE44" },
  { initials: "LC", name: "Linda C.", tags: ["CCM", "RTM"], tone: "#4E9E33", status: "On track", dot: "#6FBE44" },
  { initials: "MB", name: "Marcus B.", tags: ["APCM", "RTM"], tone: "#1E5AA8", status: "New", dot: "#2E6FB0" },
  { initials: "GT", name: "Grace T.", tags: ["RPM"], tone: "#128C8C", status: "On track", dot: "#6FBE44" },
];

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const h = (e) => setReduced(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return reduced;
};

export const DashboardMock = ({ className = "" }) => {
  const reduced = usePrefersReducedMotion();
  const [offset, setOffset] = useState(0);
  const [enrolled, setEnrolled] = useState(1248);
  const [readmits, setReadmits] = useState(14);

  useEffect(() => {
    if (reduced) return;
    const t = setInterval(() => {
      setOffset((o) => (o + 1) % POOL.length);
      setEnrolled((n) => n + Math.floor(Math.random() * 3) + 1);
      setReadmits((r) => r + (Math.random() > 0.55 ? 1 : 0));
    }, 2800);
    return () => clearInterval(t);
  }, [reduced]);

  const visible = [0, 1, 2, 3].map((i) => POOL[(offset + i) % POOL.length]);

  return (
    <div className={`relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl ${className}`} data-testid="dashboard-mock">
      <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
        <div className="flex gap-1.5">
          <span className="h-3 w-3 rounded-full bg-slate-300" />
          <span className="h-3 w-3 rounded-full bg-slate-300" />
          <span className="h-3 w-3 rounded-full bg-slate-300" />
        </div>
        <div className="ml-2 flex items-center gap-2">
          <span className="grid place-items-center h-5 w-5 rounded-md zoc-chip text-white"><Pulse size={12} weight="bold" /></span>
          <span className="text-xs font-semibold text-slate-500">ZOC Care Console</span>
        </div>
        <div className="ml-auto flex items-center gap-3 text-slate-400">
          <MagnifyingGlass size={15} />
          <Bell size={15} />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-zoc-green/12 px-2 py-0.5 text-[10px] font-bold text-[#0B7A3B]">
            <span className="h-1.5 w-1.5 rounded-full bg-zoc-green animate-pulse" /> LIVE
          </span>
        </div>
      </div>

      <div className="p-5">
        <div className="grid grid-cols-2 gap-3">
          {[
            ["Enrolled", enrolled.toLocaleString(), "#0B2A4E"],
            ["Monitored", "94%", "#128C8C"],
            ["Audit-ready", "100%", "#0B7A3B"],
            ["Readmits avoided · 30d", readmits.toString(), "#D97706"],
          ].map(([k, v, c]) => (
            <div key={k} className="rounded-xl border border-slate-100 bg-slate-50/70 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{k}</p>
              <p className="mt-1 font-display text-xl font-extrabold tabular-nums" style={{ color: c }}>{v}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-xl border border-slate-100 p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-600">Enrollment · last 12 months</p>
            <p className="text-[10px] font-bold text-[#0B7A3B]">+18%</p>
          </div>
          <div className="mt-4 flex h-24 items-end gap-1.5">
            {BARS.map((h, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                whileInView={{ height: `${h}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="flex-1 rounded-t-md"
                style={{ background: "linear-gradient(180deg,#128C8C,#6FBE44)" }}
              />
            ))}
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <AnimatePresence mode="popLayout" initial={false}>
            {visible.map((p) => (
              <motion.div
                key={p.name}
                layout
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2.5"
              >
                <span className="grid place-items-center h-8 w-8 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: p.tone }}>{p.initials}</span>
                <span className="text-sm font-medium text-[#0B2A4E]">{p.name}</span>
                <div className="ml-auto flex items-center gap-1.5">
                  {p.tags.map((t) => (
                    <span key={t} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{t}</span>
                  ))}
                  <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: p.dot }} /> {p.status}
                  </span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
